import { useCallback, useEffect, useState } from 'react';
import { useWalletConnection, useDAppKit } from '@mysten/dapp-kit-react';
import { WALLET_SESSION_STORAGE_KEY } from '../utils/dappKit';
/**
 * Compatibility shims that re-expose the legacy `@mysten/dapp-kit` v1 hook shapes
 * on top of dApp Kit 2.0, so the bulk of the app migrates with minimal churn.
 *
 * New code should prefer the native v2 hooks (`@mysten/dapp-kit-react`) and the
 * promise-based actions on the `dAppKit` instance (`useDAppKit()`) directly. These
 * shims exist only to keep the large surface of existing call sites working; they
 * can be inlined and removed incrementally.
 */
// Legacy `useCurrentWallet()` returned `{ currentWallet, isConnected, connectionStatus, … }`.
// v2's `useCurrentWallet()` returns the `UiWallet | null` directly, so we rebuild the
// legacy object from `useWalletConnection()`.
export function useCurrentWallet() {
    const connection = useWalletConnection();
    return {
        currentWallet: connection.wallet,
        isConnected: connection.isConnected,
        isConnecting: connection.isConnecting,
        isReconnecting: connection.isReconnecting,
        isDisconnected: connection.isDisconnected,
        // Legacy consumers read `connectionStatus` as 'disconnected' | 'connecting' | 'connected'.
        // v2 adds 'reconnecting'; callers already treat any non-'connected' value as "not ready".
        connectionStatus: connection.status,
    };
}
// Legacy `useAccounts()` returned the connected wallet's accounts.
export function useAccounts() {
    var _a, _b;
    const connection = useWalletConnection();
    return (_b = (_a = connection.wallet) === null || _a === void 0 ? void 0 : _a.accounts) !== null && _b !== void 0 ? _b : [];
}
// Last-resort bound on how long we report 'idle', so a caller gated on "settled" can't
// hang forever if dApp Kit never starts its restore (autoConnect disabled, storage
// unreadable). Deliberately well above dApp Kit's own 5000ms autoConnectTimeout: it
// settles only after `Promise.all([walletsRegistered, gracePeriod])`, and wallet
// registration (the Slush metadata request) has no timeout of its own, so a real restore
// can legitimately take longer than the grace period. Firing early here would reintroduce
// the mislabeled-pageview bug this hook exists to prevent.
const AUTO_CONNECT_SETTLE_TIMEOUT = 15000;
function hasSavedWalletSession() {
    try {
        return Boolean(localStorage.getItem(WALLET_SESSION_STORAGE_KEY));
    }
    catch (_a) {
        // Private-mode / blocked storage: dApp Kit falls back to in-memory storage, so
        // there is nothing to restore.
        return false;
    }
}
/**
 * Legacy `useAutoConnectWallet()` returned 'disabled' | 'idle' (attempt in progress) |
 * 'attempted' (settled). Callers (usePageViewTracker) gate on "auto-connect settled".
 *
 * `isReconnecting` alone is NOT that signal. dApp Kit's connection store starts at
 * `disconnected` and only flips to `reconnecting` inside an async task after mount, and
 * only when a saved session exists — so on the first render `isReconnecting` is always
 * false, which would report "settled, no wallet" for a returning user whose restore
 * hasn't begun. It also returns to `disconnected` once a failed restore gives up, so the
 * status alone can't distinguish "not started" from "finished".
 *
 * We therefore treat `disconnected` as settled only when we know a restore can no longer
 * be pending: it already ran (we observed `reconnecting`), there was no session to restore,
 * or the safety-net timeout elapsed.
 */
export function useAutoConnectWallet() {
    const { status } = useWalletConnection();
    // Latch that a restore actually started, because the `disconnected` dApp Kit returns to
    // after giving up is indistinguishable from the one it starts in. Set from an effect
    // rather than during render to keep the render pure; the extra render costs nothing
    // because we report 'idle' for `reconnecting` anyway. dApp Kit holds `reconnecting` for
    // at least its grace period, so this can't be missed by render batching.
    const [sawRestoreInFlight, setSawRestoreInFlight] = useState(false);
    useEffect(() => {
        if (status === 'reconnecting')
            setSawRestoreInFlight(true);
    }, [status]);
    const [settleTimedOut, setSettleTimedOut] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setSettleTimedOut(true), AUTO_CONNECT_SETTLE_TIMEOUT);
        return () => clearTimeout(timer);
    }, []);
    // 'reconnecting' = restore in flight. 'connected'/'connecting' = settled, or superseded
    // by a user-initiated connect — either way auto-connect is no longer pending.
    if (status !== 'disconnected') {
        return status === 'reconnecting' ? 'idle' : 'attempted';
    }
    return sawRestoreInFlight || settleTimedOut || !hasSavedWalletSession() ? 'attempted' : 'idle';
}
export function useSignAndExecuteTransaction() {
    const dAppKit = useDAppKit();
    return useCallback((vars, opts) => {
        dAppKit
            .signAndExecuteTransaction(vars)
            .then((res) => {
            var _a, _b, _c, _d, _e;
            // v2 result is a discriminated union keyed by `$kind`. dApp Kit resolves the
            // promise only AFTER the wallet executed the tx on-chain and dApp Kit parsed the
            // returned effects — so `$kind` is authoritative: 'Transaction' = executed
            // successfully, 'FailedTransaction' = executed but reverted on-chain. Callers use
            // `success` to decide; the digest is present on whichever branch is populated.
            const kind = res === null || res === void 0 ? void 0 : res.$kind;
            const digest = (_d = (_b = (_a = res.Transaction) === null || _a === void 0 ? void 0 : _a.digest) !== null && _b !== void 0 ? _b : (_c = res.FailedTransaction) === null || _c === void 0 ? void 0 : _c.digest) !== null && _d !== void 0 ? _d : '';
            (_e = opts === null || opts === void 0 ? void 0 : opts.onSuccess) === null || _e === void 0 ? void 0 : _e.call(opts, { digest, success: kind === 'Transaction' });
        })
            .catch((err) => {
            var _a;
            // Fires only when signing/submission itself fails (user rejection, wallet error,
            // or a submission error) — i.e. the tx did NOT execute.
            (_a = opts === null || opts === void 0 ? void 0 : opts.onError) === null || _a === void 0 ? void 0 : _a.call(opts, err instanceof Error ? err : new Error(String(err)), vars);
        });
    }, [dAppKit]);
}
